import { Translations } from './fr';

export const de: Translations = {
  // Navigation
  nav: {
    products: 'Produkte',
    faq: 'FAQ',
    login: 'Anmelden',
    contact: 'Kontakt aufnehmen',
  },

  // Hero Section
  hero: {
    title: 'End-to-End',
    titleHighlight: 'KI-Recruiting.',
    subtitleBefore: 'Beschleunigen Sie Ihre gesamte Recruiting-Pipeline. ',
    subtitleHighlight: 'Auf einmal',
    subtitleAfter: ' oder Schritt für Schritt.',
    cta: 'Kontakt aufnehmen',
    scrollHint: 'Scrollen zum Erkunden',
  },

  // Narrative Chapter
  narrative: {
    slide1: 'Talentees verwandelt Recruiting in eine End-to-End-Pipeline – vom Sourcing bis zum Angebot.',
    slide2: 'Gutes Recruiting ist eine Reihe konsistenter Entscheidungen:\ngleiche Kriterien, gleiche Nachweise, von Anfang bis Ende.',
    slide3: 'Starten Sie heute mit dem Screening.\nMorgen aktivieren Sie jeden Agenten Schritt für Schritt.',
  },

  // Capabilities Section (Recruitment Value Chain)
  capabilities: {
    title: 'Die Wertschöpfungskette der Rekrutierung',
    subtitle: 'Talentees automatisiert intelligent die Schlüsselschritte Ihres Rekrutierungsprozesses.',
    steps: {
      needDefinition: {
        step: 'Bedarfsdefinition',
        description: 'Identifizierung der Position und Kriterien',
      },
      sourcing: {
        step: 'Sourcing',
        description: 'Kandidatensuche und -ansprache',
        benefit: 'Automatische Identifizierung der besten Profile über alle Kanäle',
      },
      screening: {
        step: 'CV-Screening',
        description: 'Analyse und Sortierung von Bewerbungen',
        benefit: 'Intelligente Analyse: Score, Signale, Risiken und personalisierte Fragen',
      },
      hrInterview: {
        step: 'HR-Interview',
        description: 'Erster Austausch mit Kandidaten',
        benefit: 'Autonomes Interview oder Echtzeit-Unterstützung zur Strukturierung der Bewertung',
      },
      technicalTest: {
        step: 'Technischer Test',
        description: 'Bewertung praktischer Fähigkeiten',
        benefit: 'Automatische Generierung und Bewertung angepasster praktischer Fälle',
      },
      managerInterview: {
        step: 'Manager-Interview',
        description: 'Validierung durch den Manager',
        benefit: 'Intelligenter Assistent zur Führung und Strukturierung des Interviews',
      },
      proposal: {
        step: 'Angebot & Verhandlung',
        description: 'Endgültiges Angebot und Abschluss',
      },
    },
    legend: {
      automated: 'Automatisiert durch Talentees',
      automatedDesc: 'Spezialisierte KI für jeden Schritt',
      manual: 'Manueller Prozess',
      manualDesc: 'Von Ihrem Team verwaltet',
    },
    stats: {
      automatedSteps: 'Automatisierte Schritte',
      timeReduction: 'Reduzierung der Bearbeitungszeit',
      objectivity: 'Objektivität der Bewertungen',
    },
  },

  // Agents Section
  agents: {
    title: 'Unsere KI-Agenten',
    subtitle: 'Jeder Recruiting-Schritt wird von einem spezialisierten Agenten orchestriert.\nVom Sourcing bis zum Angebot, eine End-to-End-Pipeline.',
    available: 'Jetzt verfügbar',
    comingSoon: 'Demnächst verfügbar',
    learnMore: 'Mehr erfahren',
    keyFeatures: 'Hauptfunktionen',
    features: [
      'Intelligente Automatisierung',
      'Tiefgreifende kontextuelle Analyse',
      'Strukturierte und umsetzbare Berichte',
    ],
  },

  // Agent Names and Descriptions
  agentDetails: {
    victor: {
      name: 'Victor',
      step: 'Lebenslaufanalyse (Screening)',
      description: 'Verwandelt einen Lebenslauf in eine Entscheidung: mehrdimensionale Bewertung, Schlüsselsignale, identifizierte Risiken und personalisierte Fragen.',
    },
    nora: {
      name: 'Nora',
      step: 'Sourcing',
      description: 'Identifiziert und kontaktiert die besten Profile auf den richtigen Kanälen mit chirurgischer Präzision.',
    },
    clara: {
      name: 'Clara',
      step: 'Autonomes Interview',
      description: 'Führt das Vorstellungsgespräch End-to-End mit kontextueller Intelligenz und erstellt einen strukturierten, vergleichbaren Bericht.',
    },
    milo: {
      name: 'Milo',
      step: 'Interview-Assistent',
      description: 'Unterstützt den Recruiter in Echtzeit: führt das Gespräch, notiert wichtige Punkte und strukturiert die Bewertung.',
    },
    iris: {
      name: 'Iris',
      step: 'Fallstudie',
      description: 'Generiert und bewertet praktische Fälle, die auf die Position zugeschnitten sind, mit klarem Feedback und objektiven Kriterien.',
    },
  },

  // Pipeline Section
  pipeline: {
    label: 'Der Workflow',
    title: 'KI, die mit Ihnen arbeitet,',
    titleMuted: 'nicht an Ihrer Stelle.',
    subtitle: 'Sie behalten die Kontrolle und die Entscheidungen. Die KI übernimmt Analyse, Strukturierung und Routineaufgaben.',
    available: 'Verfügbar',
    comingSoon: 'Bald',
    steps: {
      jobs: {
        label: 'Feed with Jobs',
      },
      sourcing: {
        label: 'Sourcing',
        you: 'Sie definieren die Stelle und die Kriterien.',
        ai: 'Die KI identifiziert und kontaktiert die besten Profile über alle Kanäle.',
      },
      screening: {
        label: 'Screening',
        you: 'Sie treffen die endgültige Entscheidung.',
        ai: 'Die KI analysiert jeden Lebenslauf — Score, Signale, Risiken, empfohlene Fragen.',
      },
      interview: {
        label: 'Interview Copilot',
        you: 'Sie führen das Interview und bewerten.',
        ai: 'Die KI führt Sie in Echtzeit, strukturiert Ihre Notizen und erstellt den Bericht.',
      },
      testcase: {
        label: 'Test Case',
        you: 'Sie validieren den Fall und bewerten den Kandidaten.',
        ai: 'Die KI generiert einen stellenspezifischen Praxisfall mit objektiven Kriterien.',
      },
    },
  },

  // FAQ Section
  faq: {
    title: 'FAQ.',
    questions: [
      {
        question: 'Wie funktioniert es?',
        answer: 'Laden Sie einen Lebenslauf und eine Stellenbeschreibung hoch. In unter 30 Sekunden erstellt Talentees einen vollständigen Bericht: mehrdimensionaler Score, Profilzusammenfassung, wichtige Signale, erkannte Risiken und personalisierte Interviewfragen. Die endgültige Entscheidung liegt bei Ihnen.',
      },
      {
        question: 'Welche Daten werden gespeichert?',
        answer: 'Nur Lebensläufe und generierte Berichte werden gespeichert. Die standardmäßige Aufbewahrungsfrist beträgt 90 Tage, mit Löschung auf Anfrage jederzeit möglich. Talentees ist DSGVO-konform und keine Daten werden zum Training von Drittanbieter-Modellen verwendet.',
      },
      {
        question: 'Wie gewährleisten Sie Transparenz?',
        answer: 'Jede Bewertung wird durch direkte Zitate aus dem Lebenslauf belegt. Sie sehen genau, warum ein Profil so bewertet wird — keine Black Box. Ein vollständiger Audit-Trail ist für jede Bewertung verfügbar.',
      },
      {
        question: 'Produkt-Roadmap?',
        answer: 'Sourcing und Screening sind bereits jetzt verfügbar. Interview Copilot und Test Case werden schrittweise eingeführt — je nach Bedarf als vollständige Pipeline oder Pay as you go aktivierbar.',
      },
    ],
  },

  // Final CTA Section
  finalCta: {
    title: 'Bereit, Ihr Recruiting zu transformieren?',
    subtitle: 'Schließen Sie sich Unternehmen an, die mit KI schneller und besser einstellen.',
    cta: 'Kontakt aufnehmen',
  },

  // Footer
  footer: {
    description: 'End-to-End KI-Recruiting. Beschleunigen Sie Ihre Einstellungen mit künstlicher Intelligenz.',
    products: 'Produkte',
    company: 'Unternehmen',
    companyLinks: {
      about: 'Über uns',
      careers: 'Karriere',
    },
    support: 'Support',
    supportLinks: {
      documentation: 'Dokumentation',
    },
    legal: 'Rechtliches',
    legalLinks: {
      privacy: 'Datenschutz',
      terms: 'AGB',
      cookies: 'Cookies',
    },
    copyright: 'Talentees. Alle Rechte vorbehalten.',
  },

  // Contact Modal
  contact: {
    step1: {
      title: 'Ihre Daten',
      subtitle: 'Lassen Sie uns einander kennenlernen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'Geschäftliche E-Mail',
    },
    step2: {
      title: 'Was sind Ihre Bedürfnisse?',
      subtitle: 'Wählen Sie einen oder mehrere Dienste aus',
      options: {
        screening: 'Lebenslauf-Screening',
        sourcing: 'Kandidaten-Sourcing',
        interview: 'KI-Interviews',
        caseStudies: 'Fallstudien / Technische Tests',
      },
    },
    step3: {
      title: 'Ihr Unternehmen',
      subtitle: 'Erzählen Sie uns von Ihrem Unternehmen',
      companyName: 'Unternehmensname',
      companyDescription: 'Beschreiben Sie Ihr Unternehmen und Ihre Recruiting-Herausforderungen',
    },
    navigation: {
      step: 'Schritt',
      of: 'von',
      back: 'Zurück',
      continue: 'Weiter',
      send: 'Senden',
      sending: 'Wird gesendet...',
      pressEnter: 'Drücken Sie Enter, um fortzufahren',
    },
    success: 'Vielen Dank! Wir werden Sie sehr bald kontaktieren.',
    error: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
  },

  // Legal Pages
  legal: {
    hub: {
      title: 'Rechtliches',
      subtitle: 'Finden Sie alle unsere rechtlichen Informationen und Datenschutzrichtlinien.',
    },
    privacy: {
      title: 'Datenschutzrichtlinie',
    },
    terms: {
      title: 'Allgemeine Geschäftsbedingungen',
    },
    cookies: {
      title: 'Cookie-Richtlinie',
    },
    dpa: {
      title: 'Auftragsverarbeitungsvertrag',
    },
    candidatesPrivacy: {
      title: 'Bewerber-Datenschutz',
    },
    backToHub: 'Zurück zum Rechtlichen',
    backToHome: 'Zurück zur Startseite',
  },

  // About Page
  about: {
    label: 'Über uns',
    title: 'Wir glauben, dass das beste',
    titleMuted: 'Recruiting menschlich ist.',
    intro: "Talentees entstand aus einer einfachen Beobachtung: Recruiter verbringen zu viel Zeit mit Aufgaben mit geringem Mehrwert und zu wenig Zeit mit dem, was wirklich zählt — einen Kandidaten verstehen, ein Fit spüren, eine gute Entscheidung treffen. Unsere Mission ist es, ihnen diese Zeit zurückzugeben, mit einer KI, die mit ihnen arbeitet, nicht an ihrer Stelle.",
    valuesLabel: 'Unsere Werte',
    values: [
      {
        label: 'Der Recruiter bleibt im Mittelpunkt',
        description: "KI ist nicht dazu da, menschliches Urteilsvermögen zu ersetzen — sondern es zu verstärken. Jede Entscheidung bleibt in den Händen des Recruiters.",
      },
      {
        label: 'Vollständige Transparenz',
        description: "Keine Black Box. Jede Bewertung, jedes Signal und jede Empfehlung wird durch konkrete Belege aus der Kandidatenakte unterstützt.",
      },
      {
        label: 'Effizienz ohne Kompromisse',
        description: "Weniger Zeit für Routineaufgaben bedeutet nicht, die Standards zu senken. Wir beschleunigen den Prozess, ohne die Qualität der Einstellungen zu beeinträchtigen.",
      },
      {
        label: 'Modularität',
        description: "Jedes Team ist anders. Talentees passt sich Ihrem Workflow an — vollständige Pipeline oder Schritt für Schritt, je nach Bedarf.",
      },
    ],
    ctaLabel: 'Eine Frage? Eine Idee?',
    backHome: '← Zurück zur Startseite',
  },

  // Careers Page
  careers: {
    label: 'Karriere',
    title: 'Wir bauen etwas',
    titleMuted: 'Ambitioniertes.',
    description: 'Derzeit keine offenen Stellen — aber wenn Sie denken, einen Beitrag leisten zu können, sind wir immer offen für eine tolle Bewerbung.',
    cta: 'Initiativbewerbung',
    backHome: '← Zurück zur Startseite',
  },

  // Agents Showcase Page
  agentsShowcase: {
    title: 'Unsere KI-Agenten',
    subtitle: 'Jeder Recruiting-Schritt wird von einem spezialisierten Agenten orchestriert.\nVom Sourcing bis zum Angebot, eine End-to-End-Pipeline.',
    navHint: 'Zwischen Agenten navigieren',
  },
};
